export type HotkeyMap = Record<string, string[]>;

const MAX_LABEL_HOTKEYS = 30;

function labelHotkeyCombo(index: number): string[] {
  if (index < 10) {
    return [String(index)];
  }
  if (index < 20) {
    return ["Alt", String(index - 10)];
  }
  return ["Shift", String(index - 20)];
}

export function buildLabelHotkeys(count: number): HotkeyMap {
  const hotkeys: HotkeyMap = {};
  const safeCount = Math.max(0, Math.min(count, MAX_LABEL_HOTKEYS));

  for (let i = 0; i < safeCount; i++) {
    hotkeys[`label ${i}`] = labelHotkeyCombo(i);
  }

  return hotkeys;
}

export function isLabelHotkeyAction(action: string): boolean {
  return /^label \d+$/.test(action);
}

export const DEFAULT_HOTKEYS: HotkeyMap = {
  "Zoom In": ["Control", "i"],
  "Zoom Out": ["Control", "k"],
  "Prev Image": ["ArrowLeft"],
  "Next Image": ["ArrowRight"],
  Pan: ["p"],
  Draw: ["n"],
  Save: ["Control", "s"],
  Copy: ["Control", "c"],
  Paste: ["Control", "v"],
  Undo: ["Control", "z"],
  Redo: ["Control", "y"],
  "Hide Selected": ["Alt", "h"],
  "Hide All": ["Control", "h"],
  "Cancel Drawing": ["Escape"],
  "Finish Drawing": ["Enter"],
  "Delete Label": ["Delete"],
};

/** User-facing title in the hotkeys panel */
export const HOTKEY_LABELS: Record<string, string> = {
  "Zoom In": "Zoom in",
  "Zoom Out": "Zoom out",
  "Prev Image": "Previous image",
  "Next Image": "Next image",
  Pan: "Pan image (hand tool)",
  Draw: "Draw mode",
  Save: "Save",
  Copy: "Copy label",
  Paste: "Paste label",
  Undo: "Undo",
  Redo: "Redo",
  "Hide Selected": "Hide / show selected label",
  "Hide All": "Hide / show all labels",
  "Cancel Drawing": "Cancel current drawing",
  "Finish Drawing": "Finish polygon / polyline / line",
  "Delete Label": "Delete selected label",
};

/** Short helper text under each hotkey */
export const HOTKEY_DESCRIPTIONS: Record<string, string> = {
  Pan: "Press again while already panning — stays in pan mode",
  Draw: "Press again while already drawing — stays in draw mode",
  "Cancel Drawing": "Stops the shape you are currently drawing",
  "Finish Drawing": "Completes polygon, polyline, or line — not used for bounding boxes",
  "Delete Label": "Removes the selected annotation",
  Redo: "Also works with Control + Shift + Z",
};

const LABEL_HOTKEY_DESCRIPTION =
  "Applies this class to the selected annotation. Number matches label order from task data and cannot be remapped.";

export const HOTKEY_DISPLAY_ORDER: string[] = [
  "Draw",
  "Pan",
  "Finish Drawing",
  "Cancel Drawing",
  "Delete Label",
  "Undo",
  "Redo",
  "Copy",
  "Paste",
  "Hide Selected",
  "Hide All",
  "Zoom In",
  "Zoom Out",
  "Prev Image",
  "Next Image",
  "Save",
];

const LEGACY_KEY_MIGRATIONS: Record<string, string> = {
  Delete: "Delete Label",
  Escape: "Cancel Drawing",
  Enter: "Finish Drawing",
  Hide: "Hide Selected",
  "Redo Shift": "__remove__",
  Backspace: "__remove__",
  "Delete All Labels": "__remove__",
};

function normalizeCombo(combo: string[]): string {
  return combo
    .map((key) => (key === "Meta" ? "Control" : key.toLowerCase()))
    .sort()
    .join("+");
}

function clearLabelHotkeyMeta(): void {
  Object.keys(HOTKEY_LABELS).forEach((key) => {
    if (isLabelHotkeyAction(key)) {
      delete HOTKEY_LABELS[key];
    }
  });
  Object.keys(HOTKEY_DESCRIPTIONS).forEach((key) => {
    if (isLabelHotkeyAction(key)) {
      delete HOTKEY_DESCRIPTIONS[key];
    }
  });
}

function setLabelHotkeyMeta(labelNames: Array<{ name?: string } | string>): void {
  clearLabelHotkeyMeta();

  labelNames.forEach((label, index) => {
    if (index >= MAX_LABEL_HOTKEYS) return;
    const key = `label ${index}`;
    const name =
      typeof label === "string" ? label : label?.name ?? `Label ${index}`;
    // Match sidebar dropdown: "0 person", "1 blaster", ...
    HOTKEY_LABELS[key] = `${index} ${name}`;
    HOTKEY_DESCRIPTIONS[key] = LABEL_HOTKEY_DESCRIPTION;
  });
}

export function getLabelNameAtIndex(
  labelNames: Array<{ name?: string } | string> | null | undefined,
  index: number
): string | null {
  if (!labelNames || index < 0 || index >= labelNames.length) return null;
  const label = labelNames[index];
  if (typeof label === "string") return label;
  return label?.name ?? null;
}

/**
 * Strip every `label N` entry. Label hotkeys must be rebuilt from task labels.
 */
export function stripLabelHotkeys(hotkeys: HotkeyMap | null): HotkeyMap {
  const result: HotkeyMap = {};
  Object.entries(hotkeys || {}).forEach(([action, combo]) => {
    if (!isLabelHotkeyAction(action)) {
      result[action] = combo;
    }
  });
  return result;
}

/**
 * Keep only as many label hotkeys as we have class labels from task data.
 * Label index keys are fixed (0, 1, 2… / Alt+N / Shift+N) and not remappable —
 * so UI panels, canvas popup, and hotkeys always share the same numbers.
 * Applying a hotkey still writes the label's real id to the annotation.
 */
export function withLabelHotkeys(
  hotkeys: HotkeyMap,
  labelNames: Array<{ name?: string } | string> = []
): HotkeyMap {
  const names = Array.isArray(labelNames) ? labelNames : [];
  const count = Math.min(names.length, MAX_LABEL_HOTKEYS);
  const result = stripLabelHotkeys(hotkeys);

  // Always use the default index combos — do not preserve remapped label keys.
  Object.assign(result, buildLabelHotkeys(count));

  setLabelHotkeyMeta(names.slice(0, count));
  return result;
}

export function getHotkeyDisplayName(
  action: string,
  labelNames?: Array<{ name?: string } | string> | null
): string {
  if (isLabelHotkeyAction(action) && labelNames) {
    const index = parseInt(action.split(" ")[1], 10);
    const name = getLabelNameAtIndex(labelNames, index);
    if (name) return `${index} ${name}`;
  }
  return HOTKEY_LABELS[action] ?? action;
}

/**
 * Merge stored/API hotkeys with defaults.
 * Never keeps API `label N` slots — those are added only via withLabelHotkeys.
 */
export function mergeHotkeys(stored: HotkeyMap | null): HotkeyMap {
  const merged = { ...DEFAULT_HOTKEYS };

  if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
    return merged;
  }

  Object.entries(stored).forEach(([action, combo]) => {
    if (isLabelHotkeyAction(action)) {
      return;
    }
    const migrated = LEGACY_KEY_MIGRATIONS[action];
    if (migrated === "__remove__") {
      return;
    }
    const targetAction = migrated ?? action;
    if (Array.isArray(combo) && combo.length > 0) {
      merged[targetAction] = combo;
    }
  });

  delete merged["Delete All Labels"];

  return merged;
}

/**
 * Merge while preserving label combos from stored (used before withLabelHotkeys).
 */
export function mergeHotkeysPreservingLabelCombos(
  stored: HotkeyMap | null
): HotkeyMap {
  const merged = mergeHotkeys(stored);

  if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
    return merged;
  }

  Object.entries(stored).forEach(([action, combo]) => {
    if (
      isLabelHotkeyAction(action) &&
      Array.isArray(combo) &&
      combo.length > 0
    ) {
      merged[action] = combo;
    }
  });

  return merged;
}

export function findDuplicateHotkeyCombos(
  hotkeys: HotkeyMap
): { action: string; other: string; combo: string } | null {
  const seen = new Map<string, string>();

  for (const [action, combo] of Object.entries(hotkeys)) {
    if (!Array.isArray(combo) || combo.length === 0) {
      continue;
    }

    const signature = normalizeCombo(combo);
    const existing = seen.get(signature);
    if (existing) {
      return { action, other: existing, combo: combo.join(" + ") };
    }
    seen.set(signature, action);
  }

  return null;
}

export function sortHotkeyEntries(
  entries: [string, string][]
): [string, string][] {
  const orderIndex = new Map(
    HOTKEY_DISPLAY_ORDER.map((action, index) => [action, index])
  );

  return [...entries].sort(([a], [b]) => {
    const aIsLabel = isLabelHotkeyAction(a);
    const bIsLabel = isLabelHotkeyAction(b);

    if (aIsLabel && bIsLabel) {
      return (
        parseInt(a.split(" ")[1], 10) - parseInt(b.split(" ")[1], 10)
      );
    }
    if (aIsLabel) return 1;
    if (bIsLabel) return -1;

    const ai = orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bi = orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) {
      return ai - bi;
    }
    return a.localeCompare(b);
  });
}
